import React, { useEffect, useState } from "react";
import { CalendarDaysIcon, EnvelopeIcon } from "@heroicons/react/24/outline";
import { getMyCoach, MyCoach } from "@/api/coaching/getMyCoach";
import { cn } from "@/lib/utils";

interface YourCoachCardProps {
  /** Re-fetches whenever this changes (the active company). */
  companyKey: string | null | undefined;
}

/**
 * "Your coach" card for the bottom of the navigation rail: who the member's
 * coach is and a one-click way to book a call. The coach comes from the API
 * (the ROLE_COACH employee of the active company). Without a booking URL
 * from the API the button falls back to email.
 */
const getInitials = (name: string): string =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase();

export default function YourCoachCard({ companyKey }: YourCoachCardProps) {
  const [coach, setCoach] = useState<MyCoach | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    getMyCoach()
      .then((res) => {
        if (!cancelled) setCoach(res.coach);
      })
      .catch(() => {
        if (!cancelled) setCoach(null);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [companyKey]);

  if (!loaded || !coach) {
    return null;
  }

  const bookingUrl = coach.bookingUrl ?? null;
  const title = "Business Coach";

  return (
    <div
      className="rounded-lg border border-sidebar-border/60 bg-sidebar-accent/40 p-2.5"
      data-testid="your-coach-card"
    >
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
          {getInitials(coach.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Your coach
          </div>
          <div className="truncate text-[13px] font-semibold leading-tight">
            {coach.name}
          </div>
          <div className="truncate text-[11px] text-muted-foreground">
            {title}
          </div>
        </div>
      </div>

      <div className="mt-2 flex items-center gap-1.5">
        {bookingUrl ? (
          <a
            className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-primary px-2 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-primary/90"
            href={bookingUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
            <CalendarDaysIcon className="h-3.5 w-3.5" />
            Book a call
          </a>
        ) : (
          coach.email && (
            <a
              className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-primary px-2 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-primary/90"
              href={`mailto:${coach.email}?subject=${encodeURIComponent("Booking a coaching call")}`}
            >
              <CalendarDaysIcon className="h-3.5 w-3.5" />
              Book a call
            </a>
          )
        )}
        {coach.email && (
          <a
            aria-label={`Email ${coach.name}`}
            className={cn(
              "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-sidebar-border/60",
              "text-muted-foreground transition-colors hover:border-primary hover:text-primary",
            )}
            href={`mailto:${coach.email}`}
            title={coach.email}
          >
            <EnvelopeIcon className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  );
}
